import { useEffect, useRef, useState, type ReactElement } from "react";
import { Send, Sparkles, SquarePen, X } from "lucide-react";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { postSse } from "./sse";

export const COPILOT_PANEL_ID = "cx-copilot";

type Msg = { id: string; role: "user" | "assistant" | "error"; text: string };
const SUGGESTIONS = [
  "Quanto vendi hoje?",
  "Faturamento deste mês comparado ao anterior",
  "Estoque do produto",
];

/**
 * Painel do Copiloto somente leitura (T1.14). A resposta é texto puro (o React escapa; links já vêm removidos pela
 * API). Ciente da rota: manda o caminho da tela atual. Trocar de empresa começa uma conversa nova.
 */
export function CopilotPanel({ onClose, route }: { onClose(): void; route: string }): ReactElement {
  const { companyId } = useSession();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [text, setText] = useState("");
  const [step, setStep] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const conversationId = useRef<string | undefined>(undefined);
  const abort = useRef<AbortController | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const end = useRef<HTMLDivElement>(null);

  const reset = () => {
    abort.current?.abort();
    conversationId.current = undefined;
    setMsgs([]);
    setStep(null);
    setBusy(false);
  };
  useEffect(() => reset, []);
  useEffect(reset, [companyId]);
  // Efeitos com corpo em bloco: no Chrome 153 `scrollIntoView()` devolve uma Promise e um efeito em expressão a
  // devolveria ao React como "limpeza" (TypeError ao re-renderizar, e a tela inteira caía; CI da T1.14).
  useEffect(() => {
    input.current?.focus();
  }, []);
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: "end" });
  }, [msgs, step]);

  const ask = async (question: string) => {
    const q = question.trim();
    if (!q || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { id: crypto.randomUUID(), role: "user", text: q }]);
    const ctrl = new AbortController();
    abort.current = ctrl;
    try {
      await postSse(
        "/ai/chat",
        { message: q, conversationId: conversationId.current, context: { route } },
        {
          companyId,
          signal: ctrl.signal,
          onEvent: (e) => {
            const data = JSON.parse(e.data) as {
              label?: string;
              reply?: string;
              message?: string;
              conversationId?: string;
            };
            if (data.conversationId) conversationId.current = data.conversationId;
            if (e.event === "step") setStep(data.label ?? "Consultando…");
            else if (e.event === "message")
              setMsgs((m) => [...m, { id: crypto.randomUUID(), role: "assistant", text: data.reply ?? "" }]);
            else if (e.event === "error")
              setMsgs((m) => [
                ...m,
                { id: crypto.randomUUID(), role: "error", text: data.message ?? "Erro." },
              ]);
          },
        },
      );
    } catch (e) {
      if (!ctrl.signal.aborted)
        setMsgs((m) => [...m, { id: crypto.randomUUID(), role: "error", text: errorMessage(e) }]);
    } finally {
      if (abort.current === ctrl) {
        setBusy(false);
        setStep(null);
        input.current?.focus();
      }
    }
  };

  return (
    <aside
      id={COPILOT_PANEL_ID}
      className="copilot-panel border-start bg-body d-flex flex-column"
      aria-label="Copiloto"
    >
      <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom">
        <Sparkles size={18} aria-hidden="true" className="text-primary" />
        <h2 className="h6 mb-0 flex-grow-1">Copiloto</h2>
        <button
          type="button"
          className="btn btn-sm btn-link text-reset"
          onClick={reset}
          title="Nova conversa"
        >
          <SquarePen size={16} aria-hidden="true" />
          <span className="visually-hidden">Nova conversa</span>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-link text-reset"
          onClick={onClose}
          aria-label="Fechar o Copiloto"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      <div
        className="flex-grow-1 overflow-auto px-3 py-2"
        role="log"
        aria-live="polite"
        aria-label="Conversa com o Copiloto"
        // Região que rola: focável para quem usa só o teclado (axe scrollable-region-focusable).
        tabIndex={0}
      >
        {msgs.length === 0 && (
          <div className="text-muted small">
            <p className="mb-2">
              Pergunte sobre vendas, produtos, estoque, clientes e contas. O Copiloto só consulta (não altera
              nada) e vê apenas o que você pode ver.
            </p>
            <div className="d-flex flex-wrap gap-1">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => void ask(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m) => (
          <div
            key={m.id}
            className={`copilot-msg copilot-msg-${m.role} mb-2 p-2 rounded${
              m.role === "user"
                ? " bg-primary-subtle ms-4"
                : m.role === "error"
                  ? " bg-danger-subtle"
                  : " bg-body-tertiary me-4"
            }`}
          >
            <span className="visually-hidden">
              {m.role === "user" ? "Você: " : m.role === "error" ? "Erro: " : "Copiloto: "}
            </span>
            {m.text}
          </div>
        ))}
        {busy && (
          <div className="text-muted small d-flex align-items-center gap-2" role="status">
            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
            {step ?? "Pensando…"}
          </div>
        )}
        <div ref={end} />
      </div>
      <form
        className="border-top p-2 d-flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void ask(text);
        }}
      >
        <label htmlFor="copilot-input" className="visually-hidden">
          Pergunta ao Copiloto
        </label>
        <textarea
          id="copilot-input"
          ref={input}
          className="form-control"
          rows={2}
          maxLength={2000}
          placeholder="Pergunte algo…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void ask(text);
            }
            if (e.key === "Escape") onClose();
          }}
        />
        <button type="submit" className="btn btn-primary align-self-end" disabled={busy || !text.trim()}>
          <Send size={16} aria-hidden="true" />
          <span className="visually-hidden">Enviar</span>
        </button>
      </form>
    </aside>
  );
}
